import styled, { css } from 'styled-components';
import constants from 'src/constants';
import Button from 'src/style-guide/components/Button';
import HvSvg from 'src/components/HvSvg';

const { DEVICE } = constants.breakpoint;
const {
  SIDEBAR_WIDTH,
  EXTENDED_SIDEBAR_WIDTH,
  SIDEBAR_TRANSITION,
  TOGGLE_BUTTON_HEIGHT,
  TOGGLE_BUTTON_WIDTH,
  SIDEBAR_HEIGHT_PERCENT_PORTRAIT,
} = constants.sidebar;

export const Header = styled.div`
  padding: 16px 10px;
  ${({ theme }) => theme.mixins.font('light', 17)}
  color: ${({ theme }) => theme.colors.defaultNavy};
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.2);
  z-index: 1;

  @media ${DEVICE.MOBILE_LANDSCAPE} {
    /* if notch, add half a notch in left padding */
    padding: 16px calc(15px + env(safe-area-inset-left) / 2);

  }
`;

export const RenderingLink = styled.a`
  color: ${({ theme }) => theme.colors.mediumActionBlueTwo} !important;
  ${({ theme }) => theme.mixins.fontWeights.medium}

  &:hover {
    color: ${({ theme }) => theme.colors.darkBlueTwo} !important;
    text-decoration: underline;
  }

  &.header-link {
    font-size: ${({ theme }) => theme.utils.pxToRem(16)}rem !important;
  }

  &.learn-link {
    font-size: ${({ theme }) => theme.utils.pxToRem(14)}rem !important;
  }
`;

export const HeaderLinkCopy = styled.span`
  margin-right: 4px;
`;

export const CTASvg = styled(HvSvg)`
  & path {
    fill: white;
  }
`;

export const SidebarContainer = styled.div`
  position: relative;
  width: ${({ isOpen }) => (isOpen ? `${SIDEBAR_WIDTH}px` : 0)};
  height: 100%;
  transition: 0s;
  background-color: white;
  box-shadow: rgba(0, 0, 0, 0.15) 2px 2px 4px;

  @media ${DEVICE.MOBILE_LANDSCAPE} {
    position: relative;
    width: ${({ isOpen }) =>
      isOpen ? css`calc((${SIDEBAR_WIDTH}px) + env(safe-area-inset-left))` : 0};
  }

  @media ${DEVICE.MOBILE_PORTRAIT} {
    position: absolute;
    width: 100%;
    height: ${({ isOpen }) =>
      isOpen ? `${SIDEBAR_HEIGHT_PERCENT_PORTRAIT}%` : 0};
    bottom: 0;
  }
`;

export const ExtendedSidebarContainer = styled.div`
  position: absolute;
  left: ${SIDEBAR_WIDTH}px;
  top: 0;
  width: ${EXTENDED_SIDEBAR_WIDTH}px;
  height: 100%;
  overflow-y: auto;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.15);

  @media ${DEVICE.MOBILE} {
    left: 0;
    width: 100%;
    z-index: 1;
  }

  @media ${DEVICE.MOBILE_LANDSCAPE} {
    /* for notch on iphone*/
    padding: 0 calc(env(safe-area-inset-left) / 3);
    overflow-x: hidden;
  }
`;

export const ExtendedSidebarContent = styled.div`
  padding-top: ${({ theme }) => theme.utils.pxToRem(50)}rem;
  height: 100%;
`;

export const HeaderCloseButton = styled.div`
  display: inline-block;
  position: absolute;
  cursor: pointer;

  &:hover g g {
    fill: ${({ theme }) => theme.colors.defaultActionBlue};
  }
`;

const CloseButtonBase = css`
  display: flex;
  position: absolute;
  z-index: 1;
  bottom: 50%;
  transform: translateY(50%);
  transition: ${SIDEBAR_TRANSITION};
  border: solid 1px ${({ theme }) => theme.colors.sidebarGray};
  border-radius: 2px;
  width: ${TOGGLE_BUTTON_WIDTH}px;
  height: ${TOGGLE_BUTTON_HEIGHT}px;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.whiteLilac};
  cursor: pointer;

  &:hover {
    border: solid 1px ${({ theme }) => theme.colors.mediumActionBlueTwo};
    background-color: ${({ theme }) => theme.colors.mediumActionBlueTwo};
  }

  @media ${DEVICE.MOBILE_PORTRAIT} {
    top: -${TOGGLE_BUTTON_HEIGHT - TOGGLE_BUTTON_WIDTH}px;
    left: 50%;
    transform: translateX(-50%) rotate(-90deg);
  }
`;

export const RenderingCTAButton = styled(Button)`
  border-radius: 50%;
  height: 48px;
  width: 48px;
  min-width: unset;
  display: flex;
  padding: 0 !important;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.mediumActionBlueTwo};
  border: solid 1px ${({ theme }) => theme.colors.mediumActionBlueTwo};

  &:focus {
    box-shadow: none;
    color: ${({ theme }) => theme.colors.mediumActionBlueTwo};
    background-color: ${({ theme }) => theme.colors.mediumActionBlueTwo};
    border: 1px solid ${({ theme }) => theme.colors.mediumActionBlueTwo};
  }

  &:hover {
    border: solid 1px ${({ theme }) => theme.colors.darkBlueTwo};
    background-color: ${({ theme }) => theme.colors.darkBlueTwo};
  }
`;

export const RenderingActionButton = styled(Button)`
  border-radius: 24px;
  height: 48px;
  min-width: 120px;
  padding: 0 32px !important;
  background-color: ${({ theme }) => theme.colors.mediumActionBlueTwo};
  border: solid 1px ${({ theme }) => theme.colors.mediumActionBlueTwo};
  font-size: ${({ theme }) => theme.utils.pxToRem(16)}rem;
  ${({ theme }) => theme.mixins.fontWeights.boldImportant}

  &:hover {
    background-color: ${({ theme }) => theme.colors.darkBlueTwo};
    border: solid 1px ${({ theme }) => theme.colors.darkBlueTwo};
  }
`;

export const RenderingContainer = styled.div`
  position: absolute;
  right: -${48 + 16}px;
  bottom: 55px;
  z-index: 1;
  @media ${DEVICE.MOBILE_PORTRAIT} {
    left: 16px;
    bottom: 16px;
    display: ${({ sidebarOpen }) => (sidebarOpen ? 'none' : 'unset')};
  }
`;

export const RenderingDescription = styled.span`
  line-height: 20px;
`;

export const BlackSvg = styled(HvSvg)`
  margin: 0;
  & path {
    fill: black;
  }

  & svg {
    height: ${({ size }) => size}px;
    width: ${({ size }) => size}px;
  }
`;

export const Row = styled.div`
  margin-bottom: 20px;
  &.heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 40px;
  }
`;

export const RenderingBeforeAfterImage = styled.img`
  border-radius: 4px;
`;

export const CloseSidebarButton = styled.div`
  ${CloseButtonBase}
  display: ${({ isSidebarCloseButtonVisible }) =>
    isSidebarCloseButtonVisible ? '' : 'none'};
  right: -20px;
`;

export const CloseAllSidebarsButton = styled.div`
  ${CloseButtonBase}
  right: -${EXTENDED_SIDEBAR_WIDTH + TOGGLE_BUTTON_WIDTH}px;

  @media ${DEVICE.MOBILE_LANDSCAPE} {
    right: -${TOGGLE_BUTTON_WIDTH}px;
  }
`;
